import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { 
  Table, 
  Input, 
  Select, 
  Button, 
  Tooltip,
  Dropdown, 
  Menu
} from "antd";
import {
  SearchOutlined,
  ClearOutlined,
  MoreOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import { RxMagicWand } from "react-icons/rx";

import PageHeader from "../../../components/pageheader/PageHeader";
import EmptyState from "../../../components/emptystate/EmptyState";
import styles from "./RecommendationsMockup.module.scss";

const RecommendationsMockup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [nameFilterValue, setNameFilterValue] = useState("");
  const [typeFilterValue, setTypeFilterValue] = useState("");
  const [segmentFilterValue, setSegmentFilterValue] = useState("");

  const hasActiveFilters = () => {
    return nameFilterValue !== "" || typeFilterValue !== "" || segmentFilterValue !== "";
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setTypeFilterValue("");
    setSegmentFilterValue("");
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="Recommendation" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="Recommendation"
          createPath="/fidelity/recommendations/create"
          // createPermission="WRITE_RECOMMENDATIONS"
        />
      );
    }

    return null;
  };

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <a
          href={`/fidelity/recommendations/${record.id}/view`}
          className={styles.nameLink}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
    },
    {
      title: t("Common-Type"),
      dataIndex: "type",
    },
    {
      title: t("Common-Segments"),
      dataIndex: "segments",
      render: (segments: any[]) => 
        segments && segments.length > 0 
          ? segments.map((segment: any) => segment.name).join(", ")
          : "-",
    },
    {
      title: "Fecha implementación recomendada",
      dataIndex: "recommendedImplementationDate",
      render: (date: any) => date ? new Date(date).toLocaleDateString('es-ES') : "-",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/fidelity/recommendations/${record.id}/view`)}
              >
                {t("Common-View")}
              </Menu.Item>
              <Menu.Item
                key="edit"
                icon={<EditOutlined />}
                onClick={() => navigate(`/fidelity/recommendations/${record.id}/edit`)}
              >
                {t("Common-Edit")}
              </Menu.Item>
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                onClick={() => console.log('Delete', record.id)}
              >
                {t("Common-Delete")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("NavBar-Recommendations")}
        icon={<RxMagicWand />}
        onCreateClick={() => navigate("/fidelity/recommendations/create")}
        // createPermission="WRITE_RECOMMENDATIONS"
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => setNameFilterValue(e.target.value)}
                  value={nameFilterValue}
                />
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          <Table
            size="small"
            className={styles.table}
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id}
            pagination={{
              total: totalSize,
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            locale={{
              emptyText: getEmptyState(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RecommendationsMockup;
